import type { createRouter } from "@tanstack/react-router";
import { isSettingsPath } from "./settings-return";

/** What the router takes for `defaultViewTransition`, without the on/off form. */
type ViewTransitionOptions = Exclude<
  NonNullable<Parameters<typeof createRouter>[0]["defaultViewTransition"]>,
  boolean
>;

/** The part of an address that names the section: "/chats/new" and "/chats/monthly-close" are both "chats". */
export const sectionOf = (pathname: string): string => pathname.split("/")[1] ?? "";

/**
 * Whether going from one address to another changes the scene: the page's whole subject, such as
 * Home to Chats, or sign-in to sign-up. Moving inside a section (one chat to another, the list to a
 * chat, one connection to the next) keeps the frame, the header and the box to write in where they
 * are, so only the content changes, and it changes at once: a page-wide transition there made the
 * panel itself shimmy and laid the old page over the new one's skeleton.
 */
export function changesScene(from: string | undefined, to: string): boolean {
  if (from === undefined) return true;
  // Opening or leaving Settings is the menu's own motion: the column widens or folds and the panel
  // gives way beside it. A page-wide crossfade on top would morph the panel's snapshots between
  // those two widths and squeeze both pages as it went.
  if (isSettingsPath(from) !== isSettingsPath(to)) return false;
  return sectionOf(from) !== sectionOf(to);
}

/** The router's view-transition option: a scene change gets the transition, nothing else does. */
export const sceneTransition: ViewTransitionOptions = {
  types: ({ fromLocation, toLocation }) =>
    changesScene(fromLocation?.pathname, toLocation.pathname) ? [] : false,
};
